import React, {useEffect, useMemo, useState} from "react";
import {
    Box, Typography, Tabs, Tab, TextField, Button, Paper,
    FormControlLabel, Checkbox, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import {API_BASE} from "../../apiBase";
import EvaluationTab from "./EvaluationTab";
import LLMModelsTab from "./LLMModelsTab";
import UserApiKeysDialog from "./UserApiKeysTab";
import OrganizationApiKeysDialog from "./OrganizationApiKeysDialog";
import LogsTab from "./LogsTab";
import BackupTab from "./BackupTab";
import PatchManagementTab from "./PatchManagementTab";
import MaintenanceTab from "./MaintenanceTab";

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

export default function AdminPage() {
    const headers = useAuthHeaders();
    const [tab, setTab] = useState(0);
    const [me, setMe] = useState(null);
    const [loadingMe, setLoadingMe] = useState(true);

    // LLM options from backend
    const [llmOptions, setLlmOptions] = useState({providers: [], models: {}});
    const [loadingOptions, setLoadingOptions] = useState(true);

    // Create User form state
    const [form, setForm] = useState({
        username: "", email: "", password: "",
        is_admin: false, is_paid: false, advanced: false,
        is_test_account: false, test_responsible_person: "",
        // provider/model are independent from "advanced"
        provider: "gemini",
        model: "gemini-3.1-flash-lite",
    });
    const [creating, setCreating] = useState(false);
    const [createMsg, setCreateMsg] = useState("");

    // Fetch LLM options from backend
    useEffect(() => {
        (async () => {
            try {
                setLoadingOptions(true);
                const res = await fetch(`${API_BASE}/llm/options`);
                if (res.ok) {
                    const data = await res.json();
                    const providers = Array.isArray(data.providers) ? data.providers : [];
                    const models = (data.models && typeof data.models === "object") ? data.models : {};
                    setLlmOptions({providers, models});
                    // If current provider/model are not valid, set sensible defaults
                    const firstProvider = providers[0] || "openai";
                    const provider = providers.includes(form.provider) ? form.provider : firstProvider;
                    const modelsFor = models[provider] || [];
                    const firstModel = modelsFor[0] || "gpt-5.4";
                    const model = modelsFor.includes(form.model) ? form.model : firstModel;
                    setForm(prev => ({...prev, provider, model}));
                } else {
                    // keep defaults; UI will show text inputs if nothing loads
                    setLlmOptions({providers: [], models: {}});
                }
            } catch {
                setLlmOptions({providers: [], models: {}});
            } finally {
                setLoadingOptions(false);
            }
        })();
    }, [headers]); // once per mount / token change

    // When provider changes, keep model in sync with available ones
    useEffect(() => {
        const modelsFor = llmOptions.models[form.provider] || [];
        if (modelsFor.length && !modelsFor.includes(form.model)) {
            setForm(prev => ({...prev, model: modelsFor[0]}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.provider, llmOptions.models]);


    // Contacts state
    const [contacts, setContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [handledFilter, setHandledFilter] = useState("all"); // all|true|false
    const [selectedContact, setSelectedContact] = useState(null);
    // Users state
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userQuery, setUserQuery] = useState("");
    const [usersMsg, setUsersMsg] = useState("");
    const [deletingUserId, setDeletingUserId] = useState(null);
    // Per-row draft edits keyed by user id
    const [drafts, setDrafts] = useState({});
    const usersMsgIsSuccess = usersMsg.startsWith("✅") || usersMsg.startsWith("Deleted user ");
    const unresolvedContacts = contacts.filter((m) => !m.handled).length;
    const resolvedContacts = contacts.filter((m) => m.handled).length;

    // Change password state for admin
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
    const [passwordMsg, setPasswordMsg] = useState("");
    const [changingPassword, setChangingPassword] = useState(false);

    // Change password state for users
    const [changeUserPasswordDialog, setChangeUserPasswordDialog] = useState(null); // user object when open
    const [userNewPassword, setUserNewPassword] = useState("");
    const [userPasswordMsg, setUserPasswordMsg] = useState("");

    // Organizations state
    const [organizations, setOrganizations] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);
    const [orgsMsg, setOrgsMsg] = useState("");
    const [createOrgDialog, setCreateOrgDialog] = useState(false);
    const [editOrgDialog, setEditOrgDialog] = useState(null); // org object when editing
    const [orgForm, setOrgForm] = useState({
        name: "",
        description: "",
        default_provider: "openai",
        default_model: "gpt-5.4"
    });
    const [assignUserDialog, setAssignUserDialog] = useState(null); // org object when assigning
    const [assignForm, setAssignForm] = useState({ user_id: "", mode: "basic" });

    // API key management for organizations
    const [manageApiKeysDialog, setManageApiKeysDialog] = useState(null); // org object when managing
    
    // API key management for users
    const [manageUserApiKeysDialog, setManageUserApiKeysDialog] = useState(null); // user object when managing

    useEffect(() => {
        (async () => {
            setLoadingMe(true);
            const res = await fetch(`${API_BASE}/profile`, {headers});
            if (res.ok) {
                const data = await res.json();
                setMe(data);
            } else {
                setMe(null);
            }
            setLoadingMe(false);
        })();
    }, [headers]);

    const fetchContacts = async () => {
        setContactsLoading(true);
        const params = new URLSearchParams();
        if (handledFilter === "true" || handledFilter === "false") {
            params.set("handled", handledFilter);
        }
        const res = await fetch(`${API_BASE}/contact?${params.toString()}`, {headers});
        if (res.ok) {
            const data = await res.json();
            setContacts(data.results || []);
        } else {
            setContacts([]);
        }
        setContactsLoading(false);
    };

    useEffect(() => {
        if (tab === 3) fetchContacts(); /* Contacts tab (moved to index 3) */
    }, [tab, handledFilter]);

    const fetchOrganizations = async () => {
        setOrgsLoading(true);
        setOrgsMsg("");
        try {
            const res = await fetch(`${API_BASE}/organizations`, { headers });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setOrganizations(Array.isArray(data) ? data : []);
            } else {
                setOrganizations([]);
                setOrgsMsg(data.error || "Failed to load organizations");
            }
        } catch {
            setOrganizations([]);
            setOrgsMsg("Server error");
        } finally {
            setOrgsLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 2) fetchOrganizations(); /* Organizations tab */
    }, [tab]);

    const fetchUsers = async () => {
        setUsersLoading(true);
        setUsersMsg("");
        try {
            const params = new URLSearchParams();
            if (userQuery.trim()) params.set("q", userQuery.trim());
            params.set("limit", "100");
            const res = await fetch(`${API_BASE}/admin/users?${params.toString()}`, {headers});
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                const results = Array.isArray(data.results) ? data.results : [];
                setUsers(results);
                // Initialize drafts from server values
                const d = {};
                results.forEach(u => {
                    d[u.id] = {
                        username: u.username,
                        email: u.email,
                        is_admin: !!u.is_admin,
                        is_paid: !!u.is_paid,
                        advanced: !!u.advanced,
                        is_test_account: !!u.is_test_account,
                        test_responsible_person: u.test_responsible_person || "",
                        provider: u.provider,
                        model: u.model,
                        organization_id: u.organization_id || "",
                        mode: u.mode || "advanced",
                    };
                });
                setDrafts(d);
            } else {
                setUsers([]);
                setUsersMsg(data.error || "Failed to load users.");
            }
        } catch {
            setUsers([]);
            setUsersMsg("Server error.");
        } finally {
            setUsersLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 1) {
            fetchUsers(); /* Users tab */
            fetchOrganizations(); /* Need orgs for dropdown */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const updateDraft = (id, patch) => {
        setDrafts(prev => ({...prev, [id]: {...prev[id], ...patch}}));
    };

    const hasChanges = (u) => {
        const d = drafts[u.id] || {};
        return ["username", "email", "is_admin", "is_paid", "advanced", "mfa_enabled", "is_test_account", "test_responsible_person", "provider", "model", "organization_id", "mode"]
            .some(k => {
                // Handle empty string vs null for organization_id and test_responsible_person
                if (k === "organization_id" || k === "test_responsible_person") {
                    const dVal = d[k] === "" ? null : d[k];
                    const uVal = u[k] === "" ? null : u[k];
                    return dVal !== uVal;
                }
                return d[k] !== u[k];
            });
    };

    const saveUser = async (u) => {
        const d = drafts[u.id];
        if (!d) return;
        setUsersMsg("");
        try {
            // First update basic user fields
            const res = await fetch(`${API_BASE}/admin/users/${u.id}`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({
                    username: d.username,
                    email: d.email,
                    is_admin: d.is_admin,
                    is_paid: d.is_paid,
                    advanced: d.advanced,
                    mfa_enabled: d.mfa_enabled,
                    is_test_account: d.is_test_account,
                    test_responsible_person: d.test_responsible_person || null,
                    provider: d.provider,
                    model: d.model,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setUsersMsg(data.error || "Failed to save user.");
                return;
            }

            // If organization_id or mode changed, update that separately
            const orgIdChanged = (d.organization_id === "" ? null : d.organization_id) !== (u.organization_id === "" ? null : u.organization_id);
            const modeChanged = d.mode !== u.mode;

            if (orgIdChanged || modeChanged) {
                console.log('Updating organization/mode:', {
                    userId: u.id,
                    oldOrgId: u.organization_id,
                    newOrgId: d.organization_id,
                    oldMode: u.mode,
                    newMode: d.mode
                });

                const orgPayload = {
                    organization_id: d.organization_id === "" || d.organization_id === null ? null : parseInt(d.organization_id) || null,
                    mode: d.mode
                };

                console.log('Sending payload:', orgPayload);

                const orgRes = await fetch(`${API_BASE}/users/${u.id}/organization`, {
                    method: "PUT",
                    headers,
                    body: JSON.stringify(orgPayload),
                });

                const orgData = await orgRes.json().catch(() => ({}));
                console.log('Organization update response:', orgRes.status, orgData);

                if (!orgRes.ok) {
                    setUsersMsg(orgData.error || "Failed to update organization assignment.");
                    return;
                }
            }

            // Refresh users to get updated data
            await fetchUsers();
            setUsersMsg(`✅ Saved changes for "${d.username}" (#${u.id}).`);
        } catch {
            setUsersMsg("Server error.");
        }
    };

    const resetUser = (u) => {
        setDrafts(prev => ({
            ...prev, [u.id]: {
                username: u.username,
                email: u.email,
                is_admin: !!u.is_admin,
                is_paid: !!u.is_paid,
                advanced: !!u.advanced,
                provider: u.provider,
                model: u.model,
                organization_id: u.organization_id || "",
                mode: u.mode || "advanced",
            }
        }));
    };

    const deleteUser = async (u) => {
        const confirmed = window.confirm(
            `Delete user "${u.username}" (#${u.id}) and all associated data from the database and file system? This cannot be undone.`
        );
        if (!confirmed) return;

        setDeletingUserId(u.id);
        setUsersMsg("");
        try {
            const res = await fetch(`${API_BASE}/admin/users/${u.id}`, {
                method: "DELETE",
                headers,
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setUsers(prev => prev.filter(x => x.id !== u.id));
                setDrafts(prev => {
                    const next = {...prev};
                    delete next[u.id];
                    return next;
                });
                setUsersMsg(`Deleted user "${u.username}" (#${u.id}) and associated data.`);
            } else {
                setUsersMsg(data.error || "Failed to delete user.");
            }
        } catch {
            setUsersMsg("Server error.");
        } finally {
            setDeletingUserId(null);
        }
    };

    if (loadingMe) {
        return (
            <Box p={4} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress/>
            </Box>
        );
    }
    if (!me?.is_admin) {
        return (
            <Box p={4}>
                <Typography variant="h5" color="error">
                    Admins only.
                </Typography>
            </Box>
        );
    }

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateMsg("");
        try {
            const res = await fetch(`${API_BASE}/admin/users`, {
                method: "POST",
                headers,
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setCreateMsg(`✅ Created user "${data.username}" (#${data.id}).`);
                setForm({
                    username: "", email: "", password: "",
                    is_admin: false, is_paid: false, advanced: false,
                    is_test_account: false, test_responsible_person: "",
                    provider: "gemini",
                    model: "gemini-3.1-flash-lite",
                });
            } else {
                setCreateMsg(data.error || "Failed to create user.");
            }
        } catch {
            setCreateMsg("Server error.");
        } finally {
            setCreating(false);
        }
    };

    const markHandled = async (msgId) => {
        const res = await fetch(`${API_BASE}/contact/${msgId}/handled`, {
            method: "PATCH",
            headers,
        });
        if (res.ok) {
            setContacts((prev) => prev.map((m) => m.id === msgId ? {...m, handled: true} : m));
            setSelectedContact((prev) => prev && prev.id === msgId ? {...prev, handled: true} : prev);
        }
    };

    const handleChangeOwnPassword = async (e) => {
        e.preventDefault();
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            setPasswordMsg("New passwords do not match");
            return;
        }
        setChangingPassword(true);
        setPasswordMsg("");
        try {
            const res = await fetch(`${API_BASE}/admin/change-password`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({
                    old_password: passwordForm.old_password,
                    new_password: passwordForm.new_password
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setPasswordMsg("✅ Password changed successfully");
                setPasswordForm({ old_password: "", new_password: "", confirm_password: "" });
                setTimeout(() => setShowChangePassword(false), 1500);
            } else {
                setPasswordMsg(data.error || "Failed to change password");
            }
        } catch {
            setPasswordMsg("Server error");
        } finally {
            setChangingPassword(false);
        }
    };

    const handleChangeUserPassword = async (user) => {
        if (!userNewPassword) {
            setUserPasswordMsg("Password cannot be empty");
            return;
        }
        setUserPasswordMsg("");
        try {
            const res = await fetch(`${API_BASE}/admin/users/${user.id}/password`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({ new_password: userNewPassword }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setUserPasswordMsg(`✅ ${data.message || "Password changed successfully"}`);
                setUserNewPassword("");
                setTimeout(() => setChangeUserPasswordDialog(null), 1500);
            } else {
                setUserPasswordMsg(data.error || "Failed to change password");
            }
        } catch {
            setUserPasswordMsg("Server error");
        }
    };

    const handleCreateOrganization = async (e) => {
        e.preventDefault();
        setOrgsMsg("");
        try {
            const res = await fetch(`${API_BASE}/organizations`, {
                method: "POST",
                headers,
                body: JSON.stringify(orgForm),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setOrgsMsg(`✅ Created organization "${data.name}"`);
                setCreateOrgDialog(false);
                setOrgForm({ name: "", description: "", default_provider: "openai", default_model: "gpt-5.4" });
                fetchOrganizations();
            } else {
                setOrgsMsg(data.error || "Failed to create organization");
            }
        } catch {
            setOrgsMsg("Server error");
        }
    };

    const handleAssignUser = async (e) => {
        e.preventDefault();
        if (!assignForm.user_id) {
            setOrgsMsg("Please select a user");
            return;
        }
        setOrgsMsg("");
        try {
            const res = await fetch(`${API_BASE}/users/${assignForm.user_id}/organization`, {
                method: "PUT",
                headers,
                body: JSON.stringify({
                    organization_id: assignUserDialog.id,
                    mode: assignForm.mode
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setOrgsMsg(`✅ ${data.message}`);
                setAssignUserDialog(null);
                setAssignForm({ user_id: "", mode: "basic" });
                fetchOrganizations();
            } else {
                setOrgsMsg(data.error || "Failed to assign user");
            }
        } catch {
            setOrgsMsg("Server error");
        }
    };

    const handleOpenApiKeysDialog = async (org) => {
        setManageApiKeysDialog(org);
    };

    return (
        <Box p={3} maxWidth={2000} mx="auto">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4">Admin Panel</Typography>
                <Button variant="outlined" onClick={() => setShowChangePassword(true)}>
                    Change My Password
                </Button>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{mb: 2}}>
                <Tab label="Create User"/>
                <Tab label="Users"/>
                <Tab label="Organizations"/>
                <Tab label="Contact Messages"/>
                <Tab label="Evaluation Tests"/>
                <Tab label="LLM Models"/>
                <Tab label="Logs"/>
                <Tab label="Backup"/>
                <Tab label="Patch Management"/>
                <Tab label="Maintenance"/>
            </Tabs>

            {tab === 0 && (
                <Paper sx={{p: 3}}>
                    <Typography variant="h6" mb={2}>Create User</Typography>
                    <form onSubmit={handleCreateUser}>
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                            <TextField label="Username" value={form.username}
                                       onChange={(e) => setForm({...form, username: e.target.value})}/>
                            <TextField label="Email" value={form.email}
                                       onChange={(e) => setForm({...form, email: e.target.value})}/>
                            <TextField label="Password" type="password" value={form.password}
                                       onChange={(e) => setForm({...form, password: e.target.value})}/>
                            <Box/>
                            {/* Group flags on a single full-width row so provider/model share the next row */}
                            <Box gridColumn="1 / -1" display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <FormControlLabel control={<Checkbox checked={form.is_admin}
                                                                     onChange={(e) => setForm({
                                                                         ...form,
                                                                         is_admin: e.target.checked
                                                                     })}/>} label="Admin"/>
                                <FormControlLabel control={<Checkbox checked={form.is_paid}
                                                                     onChange={(e) => setForm({
                                                                         ...form,
                                                                         is_paid: e.target.checked
                                                                     })}/>} label="Paid"/>
                                <FormControlLabel control={<Checkbox checked={form.advanced}
                                                                     onChange={(e) => setForm({
                                                                         ...form,
                                                                         advanced: e.target.checked
                                                                     })}/>} label="Advanced features"/>
                                <FormControlLabel control={<Checkbox checked={form.is_test_account}
                                                                     onChange={(e) => setForm({
                                                                         ...form,
                                                                         is_test_account: e.target.checked
                                                                     })}/>} label="Test Account"/>
                            </Box>
                            {/* ISDS A2-4.1.8: Test account responsible person */}
                            {form.is_test_account && (
                                <TextField
                                    gridColumn="1 / -1"
                                    label="Test Account Responsible Person"
                                    value={form.test_responsible_person}
                                    onChange={(e) => setForm({...form, test_responsible_person: e.target.value})}
                                    placeholder="Name and contact (e.g., John Doe - john@company.com)"
                                    required={form.is_test_account}
                                    helperText="Required for test accounts (ISDS A2-4.1.8)"
                                />
                            )}
                            {/* Provider/Model are independent of "advanced" */}
                            {loadingOptions ? (
                                <>
                                    <TextField label="Provider" value={form.provider} disabled/>
                                    <TextField label="Model" value={form.model} disabled/>
                                </>
                            ) : llmOptions.providers.length > 0 ? (
                                <>
                                    <TextField
                                        select
                                        SelectProps={{native: true}}
                                        label="Provider"
                                        value={form.provider}
                                        onChange={(e) => setForm({...form, provider: e.target.value})}
                                    >
                                        {llmOptions.providers.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        SelectProps={{native: true}}
                                        label="Model"
                                        value={form.model}
                                        onChange={(e) => setForm({...form, model: e.target.value})}
                                    >
                                        {(llmOptions.models[form.provider] || []).map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </TextField>
                                </>
                            ) : (
                                // Fallback to free text if backend options couldn't be loaded
                                <>
                                    <TextField
                                        label="Provider"
                                        value={form.provider}
                                        onChange={(e) => setForm({...form, provider: e.target.value})}
                                    />
                                    <TextField
                                        label="Model"
                                        value={form.model}
                                        onChange={(e) => setForm({...form, model: e.target.value})}
                                    />
                                </>
                            )}
                        </Box>
                        <Box mt={2} display="flex" gap={2} alignItems="center">
                            <Button type="submit" variant="contained"
                                    disabled={creating}>{creating ? "Creating..." : "Create"}</Button>
                            {createMsg && <Typography variant="body2">{createMsg}</Typography>}
                        </Box>
                    </form>
                </Paper>
            )}

            {tab === 2 && (
                <Paper sx={{p: 3}}>
                    <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                        <Typography variant="h6">Organizations</Typography>
                        <Button variant="contained" size="small" onClick={() => {
                            setCreateOrgDialog(true);
                            setOrgForm({ name: "", description: "", default_provider: "openai", default_model: "gpt-5.4" });
                        }}>
                            Create Organization
                        </Button>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchOrganizations}><RefreshIcon/></IconButton>
                        </Tooltip>
                        {orgsMsg && (
                            <Typography variant="body2" color={orgsMsg.startsWith("✅") ? "success.main" : "error"}>
                                {orgsMsg}
                            </Typography>
                        )}
                    </Box>

                    {orgsLoading ? (
                        <Box py={6} display="flex" justifyContent="center"><CircularProgress/></Box>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Default Provider</TableCell>
                                        <TableCell>Default Model</TableCell>
                                        <TableCell>Members</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {organizations.map((org) => (
                                        <TableRow key={org.id} hover>
                                            <TableCell>{org.id}</TableCell>
                                            <TableCell>{org.name}</TableCell>
                                            <TableCell>{org.description || "—"}</TableCell>
                                            <TableCell>{org.default_provider || "—"}</TableCell>
                                            <TableCell>{org.default_model || "—"}</TableCell>
                                            <TableCell>{org.member_count || 0}</TableCell>
                                            <TableCell align="right">
                                                <Box display="flex" gap={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleOpenApiKeysDialog(org)}
                                                    >
                                                        API Keys
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setAssignUserDialog(org);
                                                            setAssignForm({ user_id: "", mode: "basic" });
                                                        }}
                                                    >
                                                        Assign User
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {organizations.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No organizations found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {tab === 3 && (
                <Paper sx={{p: 3}}>
                    <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                        <Typography variant="h6">Contact Messages</Typography>
                        <Chip size="small" color="warning" variant="outlined" label={`${unresolvedContacts} unresolved`}/>
                        <Chip size="small" color="success" variant="outlined" label={`${resolvedContacts} resolved`}/>
                        <TextField
                            select
                            SelectProps={{native: true}}
                            label="Filter"
                            value={handledFilter}
                            onChange={(e) => setHandledFilter(e.target.value)}
                            sx={{width: 160}}
                        >
                            <option value="all">All</option>
                            <option value="false">Unresolved</option>
                            <option value="true">Resolved</option>
                        </TextField>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchContacts}><RefreshIcon/></IconButton>
                        </Tooltip>
                    </Box>

                    {contactsLoading ? (
                        <Box py={6} display="flex" justifyContent="center"><CircularProgress/></Box>
                    ) : (
                        <Box display="grid" gap={2}>
                            {contacts.map((m) => (
                                <Paper key={m.id} variant="outlined" sx={{p: 2}}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography
                                                variant="subtitle1">{m.full_name} • {m.email}{m.company ? ` • ${m.company}` : ""}</Typography>
                                            <Typography variant="caption"
                                                        color="text.secondary">{new Date(m.created_at).toLocaleString('de-CH', {timeZone: 'Europe/Zurich'})}</Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Button size="small" variant="outlined" onClick={() => setSelectedContact(m)}>
                                                View
                                            </Button>
                                            <Typography variant="body2"
                                                        color={m.handled ? "success.main" : "warning.main"}>
                                                {m.handled ? "Resolved" : "Unresolved"}
                                            </Typography>
                                            {!m.handled && (
                                                <Tooltip title="Mark as handled">
                                                    <IconButton
                                                        onClick={() => markHandled(m.id)}><CheckIcon/></IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box mt={1}>
                                        <Typography variant="body2" whiteSpace="pre-wrap">{m.message}</Typography>
                                    </Box>
                                </Paper>
                            ))}
                            {contacts.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No messages.</Typography>
                            )}
                        </Box>
                    )}
                    <Dialog open={Boolean(selectedContact)} onClose={() => setSelectedContact(null)} fullWidth maxWidth="md">
                        <DialogTitle>Submitted Contact Message</DialogTitle>
                        <DialogContent dividers>
                            {selectedContact && (
                                <Box display="grid" gap={2}>
                                    <Box display="grid" gridTemplateColumns="160px 1fr" gap={1}>
                                        <Typography variant="body2" color="text.secondary">Name</Typography>
                                        <Typography variant="body2">{selectedContact.full_name}</Typography>
                                        <Typography variant="body2" color="text.secondary">Email</Typography>
                                        <Typography variant="body2">{selectedContact.email}</Typography>
                                        <Typography variant="body2" color="text.secondary">Company</Typography>
                                        <Typography variant="body2">{selectedContact.company || "—"}</Typography>
                                        <Typography variant="body2" color="text.secondary">Submitted</Typography>
                                        <Typography variant="body2">{new Date(selectedContact.created_at).toLocaleString('de-CH', {timeZone: 'Europe/Zurich'})}</Typography>
                                        <Typography variant="body2" color="text.secondary">Status</Typography>
                                        <Typography variant="body2">{selectedContact.handled ? "Resolved" : "Unresolved"}</Typography>
                                        <Typography variant="body2" color="text.secondary">Source IP</Typography>
                                        <Typography variant="body2">{selectedContact.source_ip || "—"}</Typography>
                                        <Typography variant="body2" color="text.secondary">User Agent</Typography>
                                        <Typography variant="body2" sx={{wordBreak: "break-word"}}>
                                            {selectedContact.user_agent || "—"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" mb={1}>Message</Typography>
                                        <Paper variant="outlined" sx={{p: 2, bgcolor: "background.default"}}>
                                            <Typography variant="body2" whiteSpace="pre-wrap">{selectedContact.message}</Typography>
                                        </Paper>
                                    </Box>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            {selectedContact && !selectedContact.handled && (
                                <Button onClick={() => markHandled(selectedContact.id)} startIcon={<CheckIcon/>}>
                                    Mark as handled
                                </Button>
                            )}
                            <Button onClick={() => setSelectedContact(null)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            )}

            {tab === 1 && (
                <Paper sx={{p: 3, width: "100%"}}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Typography variant="h6">Users</Typography>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchUsers}><RefreshIcon/></IconButton>
                        </Tooltip>
                        {usersMsg &&
                            <Typography variant="body2" color={usersMsgIsSuccess ? "success.main" : "error"}>
                                {usersMsg}
                            </Typography>}
                    </Box>
                    {usersLoading ? (
                        <Box py={6} display="flex" justifyContent="center"><CircularProgress/></Box>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{width: "100%"}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Username</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Flags</TableCell>
                                        <TableCell>MFA</TableCell>
                                        <TableCell>Test Account</TableCell>
                                        <TableCell>Organization</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>Provider</TableCell>
                                        <TableCell>Model</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((u) => {
                                        const d = drafts[u.id] || {};
                                        const providerModels = llmOptions.models[d.provider] || [];
                                        return (
                                            <TableRow key={u.id} hover>
                                                <TableCell>{u.id}</TableCell>
                                                <TableCell sx={{minWidth: 160}}>
                                                    <TextField
                                                        size="small"
                                                        value={d.username ?? ""}
                                                        onChange={(e) => updateDraft(u.id, {username: e.target.value})}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{minWidth: 220}}>
                                                    <TextField
                                                        size="small"
                                                        value={d.email ?? ""}
                                                        onChange={(e) => updateDraft(u.id, {email: e.target.value})}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!!d.is_admin}
                                                                    onChange={(e) => updateDraft(u.id, {is_admin: e.target.checked})}
                                                                />
                                                            }
                                                            label="Admin"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!!d.is_paid}
                                                                    onChange={(e) => updateDraft(u.id, {is_paid: e.target.checked})}
                                                                />
                                                            }
                                                            label="Paid"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!!d.advanced}
                                                                    onChange={(e) => updateDraft(u.id, {advanced: e.target.checked})}
                                                                />
                                                            }
                                                            label="Advanced"
                                                        />
                                                    </Box>
                                                    {/* Also show chips for current server flags for quick visual diff */}
                                                    <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                                                        {u.is_admin && <Chip size="small" label="server: admin"
                                                                             variant="outlined"/>}
                                                        {u.is_paid && <Chip size="small" label="server: paid"
                                                                            variant="outlined"/>}
                                                        {u.advanced && <Chip size="small" label="server: advanced"
                                                                             variant="outlined"/>}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!!d.mfa_enabled}
                                                                    onChange={(e) => updateDraft(u.id, {mfa_enabled: e.target.checked})}
                                                                />
                                                            }
                                                            label="MFA"
                                                        />
                                                        {u.mfa_enabled && <Chip size="small" label="server: active" color="primary" variant="outlined"/>}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{minWidth: 240}}>
                                                    <Box display="flex" flexDirection="column" gap={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!!d.is_test_account}
                                                                    onChange={(e) => updateDraft(u.id, {is_test_account: e.target.checked})}
                                                                />
                                                            }
                                                            label="Test Account"
                                                        />
                                                        {u.is_test_account && (
                                                            <Chip
                                                                size="small"
                                                                label="⚠️ TEST"
                                                                color="warning"
                                                                variant="filled"
                                                            />
                                                        )}
                                                        {d.is_test_account && (
                                                            <TextField
                                                                size="small"
                                                                label="Responsible Person"
                                                                value={d.test_responsible_person || ""}
                                                                onChange={(e) => updateDraft(u.id, {test_responsible_person: e.target.value})}
                                                                placeholder="Name & contact"
                                                                required={d.is_test_account}
                                                                fullWidth
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{minWidth: 180}}>
                                                    <TextField
                                                        select
                                                        SelectProps={{native: true}}
                                                        size="small"
                                                        value={d.organization_id || ""}
                                                        onChange={(e) => updateDraft(u.id, {organization_id: e.target.value})}
                                                        fullWidth
                                                    >
                                                        <option value="">None</option>
                                                        {organizations.map(org => (
                                                            <option key={org.id} value={org.id}>{org.name} (#{org.id})</option>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell sx={{minWidth: 120}}>
                                                    <TextField
                                                        select
                                                        SelectProps={{native: true}}
                                                        size="small"
                                                        value={d.mode || "advanced"}
                                                        onChange={(e) => updateDraft(u.id, {mode: e.target.value})}
                                                        fullWidth
                                                    >
                                                        <option value="advanced">Advanced</option>
                                                        <option value="basic">Basic</option>
                                                    </TextField>
                                                </TableCell>
                                                <TableCell sx={{minWidth: 160}}>
                                                    {llmOptions.providers.length > 0 ? (
                                                        <TextField
                                                            select
                                                            SelectProps={{native: true}}
                                                            size="small"
                                                            value={d.provider || llmOptions.providers[0]}
                                                            onChange={(e) => {
                                                                const nextProvider = e.target.value;
                                                                const nextModels = llmOptions.models[nextProvider] || [];
                                                                const nextModel = nextModels.includes(d.model) ? d.model : nextModels[0];
                                                                updateDraft(u.id, {
                                                                    provider: nextProvider,
                                                                    model: nextModel
                                                                });
                                                            }}
                                                        >
                                                            {llmOptions.providers.map(p => (
                                                                <option key={p} value={p}>{p}</option>
                                                            ))}
                                                        </TextField>
                                                    ) : (
                                                        <TextField
                                                            size="small"
                                                            value={d.provider ?? ""}
                                                            onChange={(e) => updateDraft(u.id, {provider: e.target.value})}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{minWidth: 200}}>
                                                    {llmOptions.providers.length > 0 ? (
                                                        <TextField
                                                            select
                                                            SelectProps={{native: true}}
                                                            size="small"
                                                            value={d.model || (providerModels[0] || "")}
                                                            onChange={(e) => updateDraft(u.id, {model: e.target.value})}
                                                        >
                                                            {providerModels.map(m => (
                                                                <option key={m} value={m}>{m}</option>
                                                            ))}
                                                        </TextField>
                                                    ) : (
                                                        <TextField
                                                            size="small"
                                                            value={d.model ?? ""}
                                                            onChange={(e) => updateDraft(u.id, {model: e.target.value})}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={{whiteSpace: "nowrap"}}>
                                                    <Tooltip title="Manage API Keys">
                                                        <Button
                                                            size="small"
                                                            onClick={() => setManageUserApiKeysDialog(u)}
                                                        >
                                                            API Keys
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Change password">
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setChangeUserPasswordDialog(u);
                                                                setUserNewPassword("");
                                                                setUserPasswordMsg("");
                                                            }}
                                                        >
                                                            Password
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Save changes">
                                                                                                    <span>
                                                            <IconButton
                                                                onClick={() => saveUser(u)}
                                                                disabled={!hasChanges(u)}
                                                            >
                                                                <SaveIcon/>
                                                            </IconButton>
                                                                                                    </span>
                                                    </Tooltip>
                                                    <Tooltip title="Reset row">
                                                        <IconButton onClick={() => resetUser(u)}>
                                                            <UndoIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete account and all associated data">
                                                        <span>
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => deleteUser(u)}
                                                                disabled={deletingUserId === u.id}
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {users.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No users found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Change Own Password Dialog */}
            <Dialog open={showChangePassword} onClose={() => setShowChangePassword(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Change My Password</DialogTitle>
                <form onSubmit={handleChangeOwnPassword}>
                    <DialogContent>
                        <Box display="grid" gap={2}>
                            <TextField
                                label="Old Password"
                                type="password"
                                value={passwordForm.old_password}
                                onChange={(e) => setPasswordForm({...passwordForm, old_password: e.target.value})}
                                required
                                fullWidth
                            />
                            <TextField
                                label="New Password"
                                type="password"
                                value={passwordForm.new_password}
                                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Confirm New Password"
                                type="password"
                                value={passwordForm.confirm_password}
                                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                                required
                                fullWidth
                            />
                            {passwordMsg && (
                                <Typography variant="body2" color={passwordMsg.startsWith("✅") ? "success.main" : "error"}>
                                    {passwordMsg}
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowChangePassword(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={changingPassword}>
                            {changingPassword ? "Changing..." : "Change Password"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Change User Password Dialog */}
            <Dialog open={Boolean(changeUserPasswordDialog)} onClose={() => setChangeUserPasswordDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Change Password for {changeUserPasswordDialog?.username}
                </DialogTitle>
                <DialogContent>
                    <Box display="grid" gap={2} pt={1}>
                        <TextField
                            label="New Password"
                            type="password"
                            value={userNewPassword}
                            onChange={(e) => setUserNewPassword(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                        {userPasswordMsg && (
                            <Typography variant="body2" color={userPasswordMsg.startsWith("✅") ? "success.main" : "error"}>
                                {userPasswordMsg}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setChangeUserPasswordDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleChangeUserPassword(changeUserPasswordDialog)}>
                        Change Password
                    </Button>
                </DialogActions>
            </Dialog>

            {tab === 4 && <EvaluationTab />}

            {tab === 5 && <LLMModelsTab />}

            {tab === 6 && <LogsTab />}

            {tab === 7 && <BackupTab headers={headers} />}

            {tab === 8 && <PatchManagementTab headers={headers} />}

            {tab === 9 && <MaintenanceTab headers={headers} />}

            {/* Create Organization Dialog */}
            <Dialog open={createOrgDialog} onClose={() => setCreateOrgDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Organization</DialogTitle>
                <form onSubmit={handleCreateOrganization}>
                    <DialogContent>
                        <Box display="grid" gap={2}>
                            <TextField
                                label="Name"
                                value={orgForm.name}
                                onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Description"
                                value={orgForm.description}
                                onChange={(e) => setOrgForm({...orgForm, description: e.target.value})}
                                fullWidth
                                multiline
                                rows={2}
                            />
                            {llmOptions.providers.length > 0 ? (
                                <>
                                    <TextField
                                        select
                                        SelectProps={{native: true}}
                                        label="Default Provider"
                                        value={orgForm.default_provider}
                                        onChange={(e) => {
                                            const provider = e.target.value;
                                            const models = llmOptions.models[provider] || [];
                                            setOrgForm({
                                                ...orgForm,
                                                default_provider: provider,
                                                default_model: models[0] || ""
                                            });
                                        }}
                                        fullWidth
                                    >
                                        {llmOptions.providers.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        SelectProps={{native: true}}
                                        label="Default Model"
                                        value={orgForm.default_model}
                                        onChange={(e) => setOrgForm({...orgForm, default_model: e.target.value})}
                                        fullWidth
                                    >
                                        {(llmOptions.models[orgForm.default_provider] || []).map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </TextField>
                                </>
                            ) : (
                                <>
                                    <TextField
                                        label="Default Provider"
                                        value={orgForm.default_provider}
                                        onChange={(e) => setOrgForm({...orgForm, default_provider: e.target.value})}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Default Model"
                                        value={orgForm.default_model}
                                        onChange={(e) => setOrgForm({...orgForm, default_model: e.target.value})}
                                        fullWidth
                                    />
                                </>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">Create</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Assign User to Organization Dialog */}
            <Dialog open={Boolean(assignUserDialog)} onClose={() => setAssignUserDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Assign User to {assignUserDialog?.name}
                </DialogTitle>
                <form onSubmit={handleAssignUser}>
                    <DialogContent>
                        <Box display="grid" gap={2}>
                            <TextField
                                label="User ID"
                                type="number"
                                value={assignForm.user_id}
                                onChange={(e) => setAssignForm({...assignForm, user_id: e.target.value})}
                                required
                                fullWidth
                                helperText="Enter the user ID from the Users tab"
                            />
                            <TextField
                                select
                                SelectProps={{native: true}}
                                label="User Mode"
                                value={assignForm.mode}
                                onChange={(e) => setAssignForm({...assignForm, mode: e.target.value})}
                                fullWidth
                            >
                                <option value="basic">Basic - Cannot change global provider/model</option>
                                <option value="advanced">Advanced - Can change global provider/model</option>
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssignUserDialog(null)}>Cancel</Button>
                        <Button type="submit" variant="contained">Assign</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Manage Organization API Keys Dialog */}
            <OrganizationApiKeysDialog
                open={Boolean(manageApiKeysDialog)}
                onClose={() => setManageApiKeysDialog(null)}
                organization={manageApiKeysDialog}
            />

            {/* Manage User API Keys Dialog */}
            <UserApiKeysDialog
                open={Boolean(manageUserApiKeysDialog)}
                onClose={() => setManageUserApiKeysDialog(null)}
                user={manageUserApiKeysDialog}
            />

        </Box>
    );
}
